import { Button, Flex, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { usePublicContact } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { BrandMark } from '../components/BrandMark';
import { LANGUAGES, setLocale, type AppLocale } from '../i18n';
import { brandRamp, neutral, shadow } from '../theme';
import { ThemeToggle } from '../themeMode';

/** The language of the page, before the profile is known (it is not saved anywhere else). */
export function LanguagePicker() {
  const { t, i18n } = useTranslation();
  return (
    <Select
      variant="borderless"
      value={i18n.language.slice(0, 2)}
      onChange={(v) => setLocale(v as AppLocale)}
      options={LANGUAGES}
      aria-label={t('shell.language')}
    />
  );
}

/**
 * The sign-in screen in the layout of Microsoft's sign-in (login.microsoftonline.com) and Fluent 2:
 * one white card on a light canvas, the brand at its top, the title in Title 3, a line of purpose,
 * the primary action at the end of the card, the legal line and the support address at the bottom
 * corner. Keycloak's login pages (theme 1.1.0) continue in the same card.
 */
export function SignInPage() {
  const { t } = useTranslation();
  const contact = usePublicContact();
  const { signIn } = useAuth();
  return (
    <Flex
      vertical
      align="center"
      justify="center"
      className="wm-signin"
      style={{
        minHeight: '100vh',
        padding: '48px 16px 64px',
        background: `radial-gradient(900px 520px at 12% 8%, ${brandRamp[160]} 0%, transparent 70%), radial-gradient(800px 520px at 92% 96%, var(--wm-signin-glow) 0%, transparent 70%), ${neutral.background3}`,
      }}
    >
      <main
        className="wm-signin-card"
        style={{
          width: '100%',
          maxWidth: 440,
          boxSizing: 'border-box',
          padding: 44,
          background: neutral.background1,
          borderRadius: 4,
          boxShadow: shadow.shadow16,
        }}
      >
        <Flex align="center" gap={10} style={{ marginBottom: 24 }}>
          <BrandMark size={28} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px' }}>
              {t('app.brand')}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12, lineHeight: '16px' }}>
              {t('app.tagline')}
            </Typography.Text>
          </div>
        </Flex>
        <Typography.Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          {t('signIn.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ margin: '8px 0 24px' }}>
          {t('signIn.subtitle')}
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 24 }}>
          {t('signIn.sso')}
        </Typography.Paragraph>
        <Flex justify="flex-end">
          <Button
            type="primary"
            size="large"
            onClick={() => void signIn()}
            style={{ minWidth: 108 }}
          >
            {t('app.signIn')}
          </Button>
        </Flex>
        <Flex align="center" gap={4} style={{ marginTop: 24, marginInlineStart: -8 }}>
          <LanguagePicker />
          <ThemeToggle />
        </Flex>
      </main>
      <Typography.Text
        className="wm-signin-footer"
        type="secondary"
        style={{ position: 'fixed', insetBlockEnd: 12, insetInlineEnd: 16, fontSize: 12 }}
      >
        {t('signIn.footer')}
        {' · '}
        <Link to="/privacy">{t('privacy.link')}</Link>
        {contact.data?.support && (
          <>
            {' · '}
            {t('signIn.help')} <a href={`mailto:${contact.data.support}`}>{contact.data.support}</a>
          </>
        )}
      </Typography.Text>
    </Flex>
  );
}
